import type { Edge, Node } from "@xyflow/react";
import type { ProjectAgentAssignment, ProjectWire, RepositoryView } from "@baton/contracts";
import type { DecryptedProjectGraph } from "../projects-view.js";
import type { ActivityEvent, ActivityState } from "./activity.js";

/**
 * Pure adapter: an already server-filtered, already decrypted ProjectGraph becomes
 * React Flow nodes and edges with deterministic identifiers and deterministic
 * positions, optionally overridden by the project's shared canvas layout. Nothing here
 * fetches, sorts by wall-clock or reads the DOM, so unit tests can assert the exact
 * graph the cockpit will draw.
 *
 * Layout: git repositories form a grey row along the top; agents sit in a row below.
 * The project itself is not a block (it lives in the sidebar). Assignments run from a
 * repository's bottom handle to an agent's top handle; wires run agent-right to
 * agent-left and mean the two agents talk to each other.
 *
 * Only routing-level fields reach node data. Message subjects, message bodies, key
 * material, bearer tokens and local paths must never be placed on a node.
 */

export const REPOSITORY_NODE_WIDTH = 224;
export const REPOSITORY_NODE_HEIGHT = 224;
export const AGENT_NODE_WIDTH = 232;
export const AGENT_NODE_HEIGHT = 176;

export const COLUMN_GAP = 28;
/** Agents sit further apart so a wire between neighbours is a visible line, not a seam. */
export const AGENT_COLUMN_GAP = 112;
export const ROW_GAP = 120;
export const REPOSITORY_ROW_Y = 0;
export const AGENT_ROW_Y = REPOSITORY_NODE_HEIGHT + ROW_GAP;

export interface RepositoryNodeData extends Record<string, unknown> {
  kind: "repository";
  repositoryId: string;
  ownerHandle: string;
  name: string;
  permission: string;
  defaultBranch: string;
  isPrivate: boolean;
  archived: boolean;
  agentCount: number;
}

export interface AgentAssignmentRef {
  assignmentId: string;
  repositoryId: string | null;
}

export interface AgentNodeData extends Record<string, unknown> {
  kind: "agent";
  agentId: string;
  handle: string;
  role: string | null;
  ownerUserId: string;
  /** Handle of the member who owns the agent, or null when that user is not in the member list. */
  ownerHandle: string | null;
  /** Canvas identity chosen when the agent was created; routing-level, never secret. */
  color: string;
  icon: string;
  ownedByViewer: boolean;
  /** Every visible assignment of this agent in this project; an agent without one has no node. */
  assignments: AgentAssignmentRef[];
  projectWide: boolean;
  repositoryIds: string[];
  /** The viewer's own general agent: the block the dashboard chats from. */
  isViewerGeneral: boolean;
  /** Transient routing state for a packet whose other endpoint is not on this canvas. */
  liveState: ActivityState | null;
}

export type BatonNodeData = RepositoryNodeData | AgentNodeData;
export type BatonNode = Node<BatonNodeData>;

export type EdgeRelation = "assignment" | "wire" | "traffic";

export interface BatonEdgeData extends Record<string, unknown> {
  relation: EdgeRelation;
  label: string;
  event: ActivityEvent | null;
  /** assignment: the assignment id; wire: the wire id; traffic: null. */
  refId: string | null;
  sourceAgentId: string | null;
  targetAgentId: string | null;
  createdByUserId: string | null;
  /** Whether the viewer may delete this edge (wire or assignment) from the canvas. */
  removable: boolean;
}

export type BatonEdge = Edge<BatonEdgeData>;

export interface ProjectFlow {
  nodes: BatonNode[];
  edges: BatonEdge[];
  height: number;
  width: number;
}

export interface BuildOptions {
  viewerUserId: string;
  viewerGeneralAgentId?: string | null;
  isAdmin?: boolean;
  /** Persisted or locally dragged positions, keyed by node id; anything else uses the deterministic layout. */
  positions?: ReadonlyMap<string, { x: number; y: number }>;
}

export function repositoryNodeId(repositoryId: string): string { return `repository:${repositoryId}`; }
export function agentNodeId(agentId: string): string { return `agent:${agentId}`; }
export function assignmentEdgeId(assignmentId: string): string { return `assignment:${assignmentId}`; }
export function wireEdgeId(wireId: string): string { return `wire:${wireId}`; }
export function trafficEdgeId(sourceNodeId: string, targetNodeId: string): string { return `traffic:${sourceNodeId}->${targetNodeId}`; }

/** Parses a traffic edge id back into its endpoints; null for any other id. */
export function parseTrafficEdgeId(edgeId: string): { sourceNodeId: string; targetNodeId: string } | null {
  const match = /^traffic:(agent:[^>]+)->(agent:.+)$/.exec(edgeId);
  return match ? { sourceNodeId: match[1]!, targetNodeId: match[2]! } : null;
}

function compareRepositories(left: RepositoryView, right: RepositoryView): number {
  const leftKey = `${left.ownerHandle}/${left.name}`;
  const rightKey = `${right.ownerHandle}/${right.name}`;
  if (leftKey === rightKey) return left.id < right.id ? -1 : 1;
  return leftKey < rightKey ? -1 : 1;
}

interface AgentGroup {
  agent: ProjectAgentAssignment["agent"];
  assignments: ProjectAgentAssignment[];
}

/**
 * The repositories array is whatever the API decided this viewer may see. A node is
 * built for every entry and for nothing else, so a repository the server omitted has
 * no node and no edge that CSS would have to hide. A repository-scoped assignment whose
 * repository is absent is dropped for the same reason instead of being re-parented.
 * One agent is one node however many assignments it holds; each assignment is an edge.
 */
export function buildProjectFlow(graph: DecryptedProjectGraph, options: BuildOptions): ProjectFlow {
  const repositories = [...graph.repositories].sort(compareRepositories);
  const visibleRepositoryIds = new Set(repositories.map((repository) => repository.id));
  const assignments = graph.agentAssignments
    .filter((assignment) => assignment.repositoryId === null || visibleRepositoryIds.has(assignment.repositoryId));
  const groups = new Map<string, AgentGroup>();
  for (const assignment of assignments) {
    const group = groups.get(assignment.agent.id) ?? { agent: assignment.agent, assignments: [] };
    group.assignments.push(assignment);
    groups.set(assignment.agent.id, group);
  }
  const agents = [...groups.values()].sort((left, right) => {
    if (left.agent.handle === right.agent.handle) return left.agent.id < right.agent.id ? -1 : 1;
    return left.agent.handle < right.agent.handle ? -1 : 1;
  });
  const memberHandles = new Map(graph.members.map((member) => [member.userId, member.handle] as const));
  const isAdmin = options.isAdmin ?? ["owner", "admin"].includes(graph.project.role);

  const nodes: BatonNode[] = [];
  const edges: BatonEdge[] = [];

  repositories.forEach((repository, index) => {
    const scoped = assignments.filter((assignment) => assignment.repositoryId === repository.id);
    nodes.push({
      id: repositoryNodeId(repository.id),
      type: "repository",
      position: { x: index * (REPOSITORY_NODE_WIDTH + COLUMN_GAP), y: REPOSITORY_ROW_Y },
      data: {
        kind: "repository",
        repositoryId: repository.id,
        ownerHandle: repository.ownerHandle,
        name: repository.name,
        permission: repository.permission,
        defaultBranch: repository.defaultBranch,
        isPrivate: repository.private,
        archived: repository.archived,
        agentCount: new Set(scoped.map((assignment) => assignment.agent.id)).size,
      },
      draggable: true,
      selectable: true,
      deletable: false,
    });
  });

  agents.forEach((group, index) => {
    const ownedByViewer = group.agent.ownerUserId === options.viewerUserId;
    const repositoryIds = [...new Set(group.assignments
      .map((assignment) => assignment.repositoryId)
      .filter((id): id is string => id !== null))].sort();
    nodes.push({
      id: agentNodeId(group.agent.id),
      type: "agent",
      position: { x: index * (AGENT_NODE_WIDTH + AGENT_COLUMN_GAP), y: AGENT_ROW_Y },
      data: {
        kind: "agent",
        agentId: group.agent.id,
        handle: group.agent.handle,
        role: group.agent.role,
        ownerUserId: group.agent.ownerUserId,
        ownerHandle: memberHandles.get(group.agent.ownerUserId) ?? null,
        color: group.agent.color,
        icon: group.agent.icon,
        ownedByViewer,
        assignments: group.assignments
          .map((assignment) => ({ assignmentId: assignment.id, repositoryId: assignment.repositoryId }))
          .sort((left, right) => left.assignmentId < right.assignmentId ? -1 : 1),
        projectWide: group.assignments.some((assignment) => assignment.repositoryId === null),
        repositoryIds,
        isViewerGeneral: options.viewerGeneralAgentId !== undefined && options.viewerGeneralAgentId !== null
          && group.agent.id === options.viewerGeneralAgentId,
        liveState: null,
      },
      draggable: true,
      selectable: true,
      deletable: false,
    });
    for (const assignment of group.assignments) {
      if (assignment.repositoryId === null) continue;
      edges.push({
        id: assignmentEdgeId(assignment.id),
        source: repositoryNodeId(assignment.repositoryId),
        target: agentNodeId(group.agent.id),
        sourceHandle: "out",
        targetHandle: "repo",
        type: "batonMessage",
        deletable: ownedByViewer || isAdmin,
        data: {
          ...edgeData("assignment", "works in"),
          refId: assignment.id,
          removable: ownedByViewer || isAdmin,
        },
      });
    }
  });

  const agentIds = new Set(agents.map((group) => group.agent.id));
  const wires = [...(graph.canvas?.wires ?? [])]
    .filter((wire) => agentIds.has(wire.sourceAgentId) && agentIds.has(wire.targetAgentId))
    .sort((left, right) => left.createdAt.localeCompare(right.createdAt) || left.id.localeCompare(right.id));
  for (const wire of wires) {
    const removable = wire.createdByUserId === options.viewerUserId || isAdmin
      || ownsAgent(groups, wire.sourceAgentId, options.viewerUserId)
      || ownsAgent(groups, wire.targetAgentId, options.viewerUserId);
    edges.push(wireEdge(wire, removable));
  }

  if (options.positions) {
    for (const node of nodes) {
      const position = options.positions.get(node.id);
      if (position) node.position = { x: position.x, y: position.y };
    }
  }

  const width = Math.max(
    repositories.length * (REPOSITORY_NODE_WIDTH + COLUMN_GAP),
    agents.length * (AGENT_NODE_WIDTH + AGENT_COLUMN_GAP),
    REPOSITORY_NODE_WIDTH,
  );
  return { nodes, edges, height: AGENT_ROW_Y + AGENT_NODE_HEIGHT, width };
}

function ownsAgent(groups: ReadonlyMap<string, AgentGroup>, agentId: string, userId: string): boolean {
  return groups.get(agentId)?.agent.ownerUserId === userId;
}

function edgeData(relation: EdgeRelation, label: string): BatonEdgeData {
  return {
    relation,
    label,
    event: null,
    refId: null,
    sourceAgentId: null,
    targetAgentId: null,
    createdByUserId: null,
    removable: false,
  };
}

export function wireEdge(wire: Pick<ProjectWire, "id" | "sourceAgentId" | "targetAgentId" | "createdByUserId">, removable: boolean): BatonEdge {
  return {
    id: wireEdgeId(wire.id),
    source: agentNodeId(wire.sourceAgentId),
    target: agentNodeId(wire.targetAgentId),
    sourceHandle: "out",
    targetHandle: "in",
    type: "batonMessage",
    deletable: removable,
    data: {
      relation: "wire",
      label: "talks to",
      event: null,
      refId: wire.id,
      sourceAgentId: wire.sourceAgentId,
      targetAgentId: wire.targetAgentId,
      createdByUserId: wire.createdByUserId,
      removable,
    },
  };
}

/** The deterministic layout as a list of positions, used by "Tidy up" to reset the shared canvas. */
export function layoutPositions(graph: DecryptedProjectGraph, options: Omit<BuildOptions, "positions">): Array<{ nodeId: string; x: number; y: number }> {
  return buildProjectFlow(graph, options)
    .nodes.map((node) => ({ nodeId: node.id, x: Math.round(node.position.x), y: Math.round(node.position.y) }));
}

/** Attaches transient activity to the edge it belongs to, leaving untouched edges identical. */
export function withActivity(edges: BatonEdge[], events: ActivityEvent[]): BatonEdge[] {
  const byEdge = new Map(events.map((event) => [event.edgeId, event] as const));
  return edges.map((edge) => {
    const event = byEdge.get(edge.id) ?? null;
    const current = edge.data?.event ?? null;
    if (event === current) return edge;
    return { ...edge, data: { ...(edge.data as BatonEdgeData), event } };
  });
}

/**
 * Live packets between two agents that share no wire ride a transient dashed edge that
 * exists only while its event is live. Endpoints must both be on the canvas.
 */
export function trafficEdges(events: readonly ActivityEvent[], nodes: readonly BatonNode[]): BatonEdge[] {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges: BatonEdge[] = [];
  for (const event of events) {
    const parsed = parseTrafficEdgeId(event.edgeId);
    if (!parsed || !nodeIds.has(parsed.sourceNodeId) || !nodeIds.has(parsed.targetNodeId)) continue;
    edges.push({
      id: event.edgeId,
      source: parsed.sourceNodeId,
      target: parsed.targetNodeId,
      sourceHandle: "out",
      targetHandle: "in",
      type: "batonMessage",
      selectable: false,
      focusable: false,
      deletable: false,
      data: { ...edgeData("traffic", "live traffic"), event },
    });
  }
  return edges;
}
